// src/Pages/ReporteAutoDownload.jsx
import React, {
    useEffect,
    useRef,
    useState,
    useCallback,
    useMemo,
} from "react";
import {
    useParams,
    useNavigate,
    useSearchParams,
} from "react-router-dom";
import axios from "../utils/axiosConfig";
import ReporteServicio from "../Modulos/Reporteservicio";
import Barra from "../Modulos/Barra";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/* ---------------------------- CONSTANTES LOCALSTORAGE ---------------------------- */
const LS_ID_KEY = "reporte:contrato:id";
const LS_DATA_KEY = "reporte:contrato:data";

/* ---------------------------- BLOQUE DE ESTILOS QUE NECESITAS PARA EXPORTAR ---------------------------- */
/* Esto viene de tu handlePrint: columnas, fotos, tablas, etc.
   Lo inyectamos en <head> para que html2canvas lo vea.
*/
const CUSTOM_PRINT_STYLES = `
    /* ========== CONTENEDOR GLOBAL DEL REPORTE (INFO GENERAL, MAPAS, TABLA, COMPARISION) ========== */
    #reporte-html2canvas-root {
        background: #fff;
        color: #000;
        font-family: Arial, sans-serif;
        font-size: 12px;
        line-height: 1.4;
        width: 100%;
        max-width: 800px;               /* ancho tipo A4 escalada */
        margin: 0 auto;
        box-sizing: border-box;
        padding: 16px;
    }

    /* Bloques normales (info contrato, mapas, tabla, comparision, etc.) */
    .print-chunk {
        background: #fff;
        color: #000;
        max-width: 800px;
        margin: 0 auto 24px auto;
        padding: 16px;
        box-sizing: border-box;
        page-break-inside: avoid;
        break-inside: avoid;
    }

    /* ========== BLOQUE DE FOTOS PAGINADAS ========== */
    /*
        Cada "página" de eventos va envuelta en <div class="event-page">
        y SIEMPRE debe empezar en una página nueva en el PDF.
    */
    .event-page {
        background: #fff;
        page-break-before: always;  /* SIEMPRE nueva página antes de este bloque */
        break-before: page;

        /* también pedimos que no se parta internamente si alcanza */
        page-break-inside: avoid;
        break-inside: avoid;

        margin: 0 auto 24px auto;
        max-width: 800px;
        padding: 16px;
        box-sizing: border-box;
    }

    /* ========== CABECERA / INFO CONTRATO / MAPAS ========== */
    .titleLabel {        
        font-size: 16px;
        font-weight: bold;
    }

    .informacionContratoReporte {
        font-size: 12px;
    }

    .informacionContratoReporte .maps {
        margin-top: 24px; /* más compacto que 50px */
    }

    .informacionContratoReporte .row {
        display: flex;
        flex-wrap: wrap;
        margin-bottom: 8px;
    }

    .informacionContratoReporte .col {
        flex: 1;
        min-width: 150px;
        padding: 4px 6px;
        box-sizing: border-box;
        word-break: break-word;
    }

    .informacionContratoReporte .col img {
        width: 100%;
        height: auto;
        max-height: 250px;
        object-fit: cover;
        display: block;
        border: 1px solid #000;
        background: #fff;
    }

    /* ========== TABLA DE EVENTOS (seguimiento de tráfico / monitoreo) ========== */
    .tablePadre {
        border-collapse: collapse;
        width: 100%;
        margin-top: 16px;
        table-layout: auto;
        font-size: 10px;
        line-height: 1.3;

        page-break-inside: avoid;
        break-inside: avoid;
    }

    .tablePadre thead tr th {
        background: #eee;
        font-weight: bold;
        text-align: left;
        vertical-align: top;
    }

    .tablePadre th,
    .tablePadre td {
        border: 1px solid #000;
        padding: 4px;
        font-size: 10px;
        line-height: 1.3;
        vertical-align: top;
        word-break: break-word;
        white-space: normal;
    }

    .tablePadre img {
        max-width: 100%;
        height: auto;
        max-height: 160px;
        object-fit: cover;
        display: block;
        border: 1px solid #000;
        background: #fff;
    }

    /* ========== GRID DE FOTOS DE LOS EVENTOS (máx 9 por página) ========== */
    /*
        Dentro de .event-page, tú pones:
        <div class="cuadrophotos">
            ...hasta 9 .cardphoto...
        </div>

        3 columnas x 3 filas = 9 fotos por página.
    */
    .cuadrophotos {
        display: grid;
        grid-template-columns: repeat(3, 1fr); /* 3 columnas */
        gap: 16px;
        align-items: start;
        width: 100%;
        margin-top: 16px;
        box-sizing: border-box;

        /* evita que se parta la grilla a mitad de página */
        page-break-inside: avoid;
        break-inside: avoid;
    }

    /*
        ESTA ES LA CARD QUE QUIERES MÁS GRANDE
        Cambios importantes:
        - usamos min-height, NO height fija
        - aumentamos el espacio para que quepa toda la info del evento
        - dejamos que crezca si hay más texto
    */
    .cardphoto {
        background-color: #a0b5bf;
        border: 1px solid #000;
        border-radius: 4px;
        padding: 8px;
        box-sizing: border-box;
        width: 100%;

        /* más grande que antes */
        min-height: 340px;

        page-break-inside: avoid;
        break-inside: avoid;

        display: flex;
        flex-direction: column;
        justify-content: flex-start;
    }

    /* IMAGEN DEL EVENTO
       - altura fija para que todas las miniaturas se vean uniformes
       - más grande que antes para que se note mejor la evidencia
    */
    .cardphoto img,
    .cuadrophotos img {
        width: 100% !important;
        height: 220px !important;      /* subimos de 160 → 220 */
        object-fit: cover !important;
        display: block;
        border: 1px solid #000;
        border-radius: 2px;
        background: #fff;
        flex-shrink: 0;
    }

    /* TEXTO DEL EVENTO (fecha, lat/lon, nota, etc.)
       - quitamos clamps y overflow hidden
       - dejamos que fluya completo
       - si es muy largo, la tarjeta crece (por eso usamos min-height arriba)
    */
    .cardphoto .card-body {
        flex: 1;
        padding: 8px 0 0;
        font-size: 11px;          /* lo subimos de 10px a 11px para legibilidad */
        line-height: 1.45;
        color: #000;
        overflow: visible;
        white-space: normal;
        word-break: break-word;
    }

    .cardphoto .card-text {
        font-size: 11px;
        color: #000;
        margin: 0;
        white-space: normal;
        word-break: break-word;
    }

    /* ========== BLOQUE DE COMPARACIÓN / ANÁLISIS DE SEGURIDAD (antes de las fotos) ========== */
    .comparision {
        display: block;
        margin-top: 24px;
        page-break-before: auto;
        page-break-inside: avoid;
        break-inside: avoid;
        background: #fff;
    }

    .comparision h3 {
        margin: 0 0 16px 0;
        font-size: 14px;
        font-weight: bold;
    }

    .comparision .comparisionImgs {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        gap: 12px;
        margin-top: 12px;
    }

    .cardphotoComparative {
        position: relative;
        flex: 1;
        min-width: calc(50% - 12px);
        max-width: calc(50% - 12px);
        border: 1px solid #000;
        border-radius: 6px;
        padding: 8px;
        box-sizing: border-box;
        background: #fff;
        page-break-inside: avoid;
        break-inside: avoid;
    }

    .cardphotoComparative img {
        display: block;
        width: 100%;
        height: 200px;
        object-fit: cover;
        border-radius: 4px;
        border: 1px solid #000;
        background: #fff;
    }

    .cardphotoComparative.border-green {
        box-shadow: 0 0 0 2px green inset;
    }
    .cardphotoComparative.border-yellow {
        box-shadow: 0 0 0 2px yellow inset;
    }
    .cardphotoComparative.border-red {
        box-shadow: 0 0 0 2px red inset;
    }

    .cardphotoComparative::before,
    .cardphotoComparative::after {
        content: none !important;
    }

    .infoAditionalBody {
        margin-top: 8px;
        font-size: 10px;
        line-height: 1.4;
        color: #000;
        height: auto;
    }

    .infoAditionalBody p {
        margin: 0 0 4px 0;
        font-size: 10px;
    }

    .comparisionResults {
        margin-top: 16px;
        margin-bottom: 16px;
        font-size: 12px;
        line-height: 1.4;
        color: #000;
        white-space: normal;
        word-break: break-word;
        page-break-inside: avoid;
        break-inside: avoid;
    }

    /* ========== RESET SUAVE GENERAL ========== */
    #reporte-html2canvas-root,
    #reporte-html2canvas-root * {
        box-sizing: border-box;
        max-width: 100%;
    }

    #reporte-html2canvas-root img {
        max-width: 100%;
        height: auto;
    }
`;






/* ---------------------------- HELPERS ---------------------------- */
function safeParse(json) {
    try {
        return JSON.parse(json);
    } catch {
        return null;
    }
}

function getContratoFromLocalStorage() {
    const dataRaw = localStorage.getItem(LS_DATA_KEY);
    const dataObj = dataRaw ? safeParse(dataRaw) : null;
    const idFromLS =
        localStorage.getItem(LS_ID_KEY) ||
        dataObj?.id ||
        dataObj?._id ||
        null;
    return { idFromLS, dataObj };
}

async function waitStylesheetsAndFonts() {
    const links = Array.from(
        document.querySelectorAll('link[rel="stylesheet"]')
    );
    await Promise.all(
        links.map(
            (l) =>
                new Promise((res) => {
                    if (l.sheet) return res();
                    l.addEventListener("load", res, { once: true });
                    l.addEventListener("error", res, { once: true });
                })
        )
    );

    if (document.fonts?.ready) {
        try {
            await document.fonts.ready;
        } catch { /* ignore */ }
    }
}

async function waitImages(root) {
    const imgs = Array.from(root?.querySelectorAll?.("img") || []);
    const pend = imgs.filter(
        (img) => !img.complete || img.naturalWidth === 0
    );
    if (!pend.length) return;

    await Promise.all(
        pend.map(
            (img) =>
                new Promise((res) => {
                    // fuerza CORS anónimo por si acaso
                    img.crossOrigin = "anonymous";
                    img.referrerPolicy = "no-referrer";

                    const done = () => {
                        img.removeEventListener("load", done);
                        img.removeEventListener("error", done);
                        res();
                    };
                    img.addEventListener("load", done, { once: true });
                    img.addEventListener("error", done, { once: true });
                })
        )
    );
}

async function downloadElementAsPdf(el, filename = "reporte.pdf") {
    if (!el) throw new Error("No existe el contenedor del reporte");

    await sleep(200);

    // html2canvas sobre el wrapper con fondo/blanco/control ancho
    const canvas = await html2canvas(el, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#fff",
        scale: 2,
        logging: false,
        ignoreElements: (n) => n.tagName === "IFRAME" || n.tagName === "CANVAS",
    });

    const imgData = canvas.toDataURL("image/jpeg", 0.95);
    const pdf = new jsPDF({
        unit: "pt",
        format: "a4",
        orientation: "portrait",
    });

    const pageW = pdf.internal.pageSize.getWidth();   // ~595pt
    const pageH = pdf.internal.pageSize.getHeight();  // ~842pt
    const margin = 20;
    const imgW = pageW - margin * 2;
    const ratio = canvas.height / canvas.width;
    const imgH = imgW * ratio;

    if (imgH <= pageH - margin * 2) {
        pdf.addImage(
            imgData,
            "JPEG",
            margin,
            margin,
            imgW,
            imgH,
            undefined,
            "FAST"
        );
    } else {
        // paginar
        const pageCanvasH = Math.floor(
            ((pageH - margin * 2) / imgH) * canvas.height
        );
        const pageCanvas = document.createElement("canvas");
        const ctx = pageCanvas.getContext("2d");

        pageCanvas.width = canvas.width;
        pageCanvas.height = pageCanvasH;

        let y = 0;
        let first = true;
        while (y < canvas.height) {
            const sliceH = Math.min(pageCanvasH, canvas.height - y);

            ctx.clearRect(0, 0, pageCanvas.width, pageCanvas.height);
            ctx.drawImage(
                canvas,
                0,
                y,
                canvas.width,
                sliceH,
                0,
                0,
                canvas.width,
                sliceH
            );

            const sliceData = pageCanvas.toDataURL("image/jpeg", 0.95);

            if (!first) pdf.addPage();

            const sliceHScaled = (sliceH / canvas.width) * imgW;
            pdf.addImage(
                sliceData,
                "JPEG",
                margin,
                margin,
                imgW,
                sliceHScaled,
                undefined,
                "FAST"
            );

            y += sliceH;
            first = false;
        }
    }

    pdf.save(filename);
}

/* ---------------------------- COMPONENTE ---------------------------- */
export default function ReporteAutoDownload() {
    const { contratoId: contratoIdFromPath } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const reviewMode = useMemo(
        () => searchParams.get("review") === "1",
        [searchParams]
    );

    const [infoContrato, setInfoContrato] = useState([]);
    const [ultPunto, setUltPunto] = useState(-1);
    const [contractR, setContractR] = useState(-1);

    const [status, setStatus] = useState("Preparando…");
    const [error, setError] = useState(null);

    const [fontsOk, setFontsOk] = useState(false);
    const [imgsOk, setImgsOk] = useState(false);
    const [containerOk, setContainerOk] = useState(false);

    const reporteRef = useRef(null);
    const printedRef = useRef(false);

    const [resolvedId, setResolvedId] = useState(null);

    // 0. Inyectar los estilos custom en <head> una sola vez
    useEffect(() => {
        const styleTagId = "reporte-print-inline-styles";
        if (!document.getElementById(styleTagId)) {
            const styleEl = document.createElement("style");
            styleEl.id = styleTagId;
            styleEl.type = "text/css";
            styleEl.appendChild(document.createTextNode(CUSTOM_PRINT_STYLES));
            document.head.appendChild(styleEl);
        }
    }, []);

    // 1. resolver contrato
    useEffect(() => {
        const { idFromLS, dataObj } = getContratoFromLocalStorage();

        if (dataObj) {
            setInfoContrato([dataObj]);
            setResolvedId(dataObj?.id || dataObj?._id || idFromLS || null);
            setStatus("Contrato recuperado desde localStorage.");
            return;
        }

        if (idFromLS) {
            setResolvedId(idFromLS);
            setStatus("ID recuperado desde localStorage. Cargando contrato…");
            return;
        }

        if (contratoIdFromPath) {
            setResolvedId(contratoIdFromPath);
            setStatus("ID recuperado desde ruta. Cargando contrato…");
            return;
        }

        setError(
            "No se encontró un contrato seleccionado. Guarda primero en localStorage (reporte:contrato:id o reporte:contrato:data)."
        );
    }, [contratoIdFromPath]);

    // 2. fetch contrato
    const fetchContratoById = useCallback(async (id) => {
        setStatus("Cargando contrato…");

        const res = await axios.post(
            `${process.env.REACT_APP_SERVER}/operaciones/getcontratounico`,
            JSON.stringify({ contrato: id }),
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("tkn")}`,
                },
                withCredentials: true,
            }
        );

        if (res.data?.success === false) {
            throw new Error(res.data?.message || "No se pudo obtener el contrato");
        }

        const data = res.data?.data || [];
        setInfoContrato(data);

        if (data[0]) {
            localStorage.setItem(LS_DATA_KEY, JSON.stringify(data[0]));
        }

        return data;
    }, []);

    // 3. cuando tengo id, aseguro data
    useEffect(() => {
        (async () => {
            if (!resolvedId) return;

            if (!infoContrato?.[0]) {
                try {
                    await fetchContratoById(resolvedId);
                } catch (e) {
                    setError(e?.message || "Error cargando el contrato");
                    return;
                }
            }

            setUltPunto(null);
            setContractR(-1);

            setStatus(
                reviewMode ? "Renderizando para revisión…" : "Renderizando reporte…"
            );
        })();
    }, [resolvedId, reviewMode, infoContrato, fetchContratoById]);

    // 4. ref que nos pasa ReporteServicio
    const setChildRef = useCallback((ref) => {
        const node = ref?.current || ref;
        reporteRef.current = node || null;
        setContainerOk(Boolean(node));
    }, []);

    // 5. cuando ReporteServicio dice "listo"
    const onReporteReady = useCallback(async () => {
        if (printedRef.current || reviewMode) return;
        printedRef.current = true;

        setStatus("Render listo. Esperando 5s para descargar…");

        await sleep(5000);

        await waitStylesheetsAndFonts();
        setFontsOk(true);

        // asegurar imágenes cargadas y con crossOrigin
        await waitImages(reporteRef.current);
        setImgsOk(true);

        // wrapper para forzar fondo blanco y ancho A4 aprox (595pt ~ 794px @96dpi ~ usa 800px máx)
        // truco: html2canvas va a leer estilos ya inyectados arriba (CUSTOM_PRINT_STYLES)
        const elParaPDF = reporteRef.current;
        // si quieres, puedes limitar ancho visualmente:
        elParaPDF.style.maxWidth = "800px";
        elParaPDF.style.background = "#fff";

        const fileSuffix = resolvedId ? String(resolvedId) : "sin-id";

        await downloadElementAsPdf(
            elParaPDF,
            `reporte-servicio-${fileSuffix}.pdf`
        );

        setStatus("PDF generado y descargado.");

        navigate("/trafico", { replace: true });
    }, [resolvedId, navigate, reviewMode]);

    // 6. helpers manuales en modo review
    const validarRecursos = useCallback(async () => {
        try {
            setStatus("Validando estilos y fuentes…");
            await waitStylesheetsAndFonts();
            setFontsOk(true);

            setStatus("Esperando imágenes…");
            await waitImages(reporteRef.current);
            setImgsOk(true);

            setStatus("Listo para descargar.");
        } catch (e) {
            setError(e?.message || "Error validando recursos");
        }
    }, []);

    const descargarAhora = useCallback(async () => {
        try {
            setStatus("Generando PDF…");

            if (!fontsOk) {
                await waitStylesheetsAndFonts();
                setFontsOk(true);
            }

            if (!imgsOk) {
                await waitImages(reporteRef.current);
                setImgsOk(true);
            }

            const elParaPDF = reporteRef.current;
            elParaPDF.style.maxWidth = "800px";
            elParaPDF.style.background = "#fff";

            const fileSuffix = resolvedId ? String(resolvedId) : "sin-id";

            await downloadElementAsPdf(
                elParaPDF,
                `reporte-servicio-${fileSuffix}.pdf`
            );

            setStatus("PDF generado y descargado.");
        } catch (e) {
            setError(e?.message || "No se pudo generar el PDF");
        }
    }, [resolvedId, fontsOk, imgsOk]);

    // 7. debug panel
    const DebugPanel = () => (
        <div
            style={{
                position: "fixed",
                right: 16,
                bottom: 16,
                zIndex: 99999,
                background: "rgba(0,0,0,0.85)",
                color: "#fff",
                padding: 12,
                borderRadius: 12,
                maxWidth: 360,
                fontSize: 13,
            }}
        >
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
                Revisión manual
            </div>

            <ul
                style={{
                    margin: 0,
                    paddingLeft: 16,
                    lineHeight: 1.6,
                }}
            >
                <li>
                    ID resuelto: <b>{resolvedId || "—"}</b>
                </li>
                <li>
                    contrato cargado:{" "}
                    <b>{infoContrato?.[0] ? "sí" : "no"}</b>
                </li>
                <li>
                    contenedor del reporte:{" "}
                    <b>{containerOk ? "sí" : "no"}</b>
                </li>
                <li>
                    fuentes/estilos listos:{" "}
                    <b>{fontsOk ? "sí" : "no"}</b>
                </li>
                <li>
                    imágenes listas: <b>{imgsOk ? "sí" : "no"}</b>
                </li>
            </ul>

            <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: "pointer" }}>
                    Ver JSON contrato (parcial)
                </summary>
                <pre
                    style={{
                        maxHeight: 180,
                        overflow: "auto",
                        whiteSpace: "pre-wrap",
                    }}
                >
                    {JSON.stringify(infoContrato?.[0] || {}, null, 2)}
                </pre>
            </details>

            <div
                style={{
                    display: "flex",
                    gap: 8,
                    marginTop: 10,
                }}
            >
                <button
                    onClick={validarRecursos}
                    style={{
                        padding: "6px 10px",
                        borderRadius: 8,
                        border: "1px solid #555",
                        background: "#111",
                        color: "#fff",
                        cursor: "pointer",
                    }}
                >
                    Validar recursos
                </button>

                <button
                    onClick={descargarAhora}
                    disabled={!containerOk || !infoContrato?.[0]}
                    style={{
                        padding: "6px 10px",
                        borderRadius: 8,
                        border: "1px solid #2e7d32",
                        background: "#1b5e20",
                        color: "#fff",
                        cursor:
                            containerOk && infoContrato?.[0]
                                ? "pointer"
                                : "not-allowed",
                        opacity:
                            containerOk && infoContrato?.[0]
                                ? 1
                                : 0.6,
                    }}
                >
                    Descargar PDF
                </button>

                <button
                    onClick={() =>
                        navigate("/trafico", {
                            replace: true,
                        })
                    }
                    style={{
                        padding: "6px 10px",
                        borderRadius: 8,
                        border: "1px solid #b71c1c",
                        background: "#7f0000",
                        color: "#fff",
                        cursor: "pointer",
                    }}
                >
                    Salir
                </button>
            </div>
        </div>
    );

    // 8. render
    return (
        <div className="layout-wrapper layout-content-navbar usuarios">
            <div className="layout-container">
                <div className="layout-page">
                    <Barra />

                    <div className="content-wrapper">
                        <div className="container-fluid flex-grow-1 container-p-y">
                            {!error ? (
                                <div
                                    style={{
                                        fontSize: 14,
                                        opacity: 0.8,
                                        padding: 8,
                                    }}
                                >
                                    {status}{" "}
                                    {resolvedId ? `(${resolvedId})` : ""}
                                    {reviewMode && (
                                        <span
                                            style={{
                                                marginLeft: 8,
                                                fontWeight: 600,
                                            }}
                                        >
                                            [REVIEW]
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <div
                                    style={{
                                        padding: 16,
                                        color: "#b00",
                                        fontWeight: 600,
                                    }}
                                >
                                    {String(error)}
                                </div>
                            )}

                            {infoContrato?.[0] ? (
                                <>
                                    {/* wrapper controlado para html2canvas */}
                                    <div
                                        id="reporte-html2canvas-root"
                                        ref={setChildRef}
                                        style={{
                                            background: "#fff",
                                            color: "#000",
                                            // limitar ancho para que A4 no se deforme
                                            maxWidth: "800px",
                                            margin: "0 auto",
                                        }}
                                    >
                                        <ReporteServicio
                                            contrato={infoContrato[0]}
                                            ultPunto={ultPunto}
                                            contractR={contractR}
                                            // ya no le pasamos setContainerRef directo al hijo,
                                            // porque estamos usando este wrapper como ref
                                            setContainerRef={() => { }}
                                            onReady={onReporteReady}
                                            printMode
                                        />
                                    </div>

                                    {reviewMode && <DebugPanel />}
                                </>
                            ) : !error ? (
                                <div style={{ padding: 24 }}>Cargando…</div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
